import { AnimatePresence, motion } from 'framer-motion';
import { RotateCw } from 'lucide-react';
import { CourseLessonDetails } from '../../../../lib/interfaces/courses';
import { FlatLesson, LessonDirection } from '../../../../hooks/use-course-detail';
import { Button } from '../../../../ui/components/ui/button';
import LessonView from '../lesson-view/lesson-view';

export interface LessonPlayerProps {
  entry: FlatLesson;
  /** Full content of the lesson, or null while it loads or when its fetch failed. */
  content: CourseLessonDetails | null;
  /** Content failed to load — show the error state with a retry. Otherwise a null content is loading. */
  hasError: boolean;
  onRetry: () => void;
  direction: LessonDirection;
  isLastLesson: boolean;
  isCompleting: boolean;
  onComplete: () => void;
}

/**
 * Forward (1) enters from the bottom and exits to the top — the reader swipes up the stack to the
 * next lesson. Back (-1) mirrors it. Custom is read by the variants below via AnimatePresence.
 */
const variants = {
  enter: (direction: LessonDirection) => ({ y: direction > 0 ? '100%' : '-100%', opacity: 0 }),
  center: { y: 0, opacity: 1 },
  exit: (direction: LessonDirection) => ({ y: direction > 0 ? '-100%' : '100%', opacity: 0 }),
};

const LessonSkeleton = () => (
  <div
    className="bg-white rounded-2xl border border-[#E4E7EC] p-5 flex flex-col gap-4 h-full animate-pulse"
    style={{ boxShadow: '0px 4px 12.7px 0px rgba(0, 0, 0, 0.04)' }}>
    <div className="h-3 w-40 bg-[#E4E7EC] rounded-full" />
    <div className="h-6 w-64 bg-[#E4E7EC] rounded-full" />
    <div className="flex-1 bg-[#F0F4FC] rounded-xl" />
  </div>
);

const LessonError = ({ onRetry }: { onRetry: () => void }) => (
  <div
    className="bg-white rounded-2xl border border-[#E4E7EC] p-5 h-full flex flex-col items-center justify-center gap-3 text-center"
    style={{ boxShadow: '0px 4px 12.7px 0px rgba(0, 0, 0, 0.04)' }}>
    <p className="text-[16px] font-bold text-[#12282A]">Inhalt konnte nicht geladen werden</p>
    <p className="text-[14px] text-[#888] max-w-[320px]">Bitte prüfe deine Verbindung und versuche es erneut.</p>
    <Button variant="outline" onClick={onRetry} className="flex items-center gap-2 mt-1">
      <RotateCw className="w-4 h-4" />
      Erneut versuchen
    </Button>
  </div>
);

/**
 * Shows one lesson at full height and animates the transition to the next. The stack is a single
 * lesson deep: only the current lesson is mounted, sliding out as the next slides in. Content that
 * has not arrived yet shows a skeleton in the same slot, so the swipe never waits on the network.
 */
const LessonPlayer = ({ entry, content, hasError, onRetry, direction, isLastLesson, isCompleting, onComplete }: LessonPlayerProps) => {
  return (
    <div className="relative h-full min-h-0 overflow-hidden">
      <AnimatePresence custom={direction} initial={false}>
        <motion.div
          key={entry.lesson._id}
          custom={direction}
          variants={variants}
          initial="enter"
          animate="center"
          exit="exit"
          transition={{ type: 'tween', duration: 0.42, ease: [0.22, 1, 0.36, 1] }}
          className="absolute inset-0">
          {content ? (
            <LessonView
              moduleTitle={entry.moduleTitle}
              lessonTitle={entry.lesson.title}
              lesson={{ heading: content.heading, videoUrl: content.videoUrl, textHtml: content.textHtml }}
              fillHeight
              nextLabel={isLastLesson ? 'Kurs abschließen' : 'nächste Lektion'}
              isCompleting={isCompleting}
              onNext={onComplete}
            />
          ) : hasError ? (
            <LessonError onRetry={onRetry} />
          ) : (
            // No content and no error means the fetch is still in flight.
            <LessonSkeleton />
          )}
        </motion.div>
      </AnimatePresence>
    </div>
  );
};

export default LessonPlayer;
